import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Grabado como servicio aparte (inventario unificado).
 *
 * Los productos en «modo servicio» se añaden al carrito con una sola variante
 * por color y la propiedad oculta _mr_servicio, que guarda la variante del
 * servicio «Grabado láser» elegida en la ficha. Este módulo se asegura de que
 * cada una de esas líneas lleve debajo su línea anidada del servicio, con la
 * misma cantidad:
 *
 *   - después de cada cambio del carrito (alta desde la ficha, cambio de
 *     cantidad, etc.),
 *   - al cargar cualquier página,
 *   - y justo antes de ir a pagar, por si el cliente fue muy rápido.
 *
 * Shopify quita las líneas anidadas cuando se quita la línea principal, y el
 * carrito no muestra controles de cantidad en la del servicio
 * (snippets/mr-cart-trust.liquid), así que el grabado no se puede perder.
 */

const PROPERTY = '_mr_servicio';
const SOURCE = 'mr-engraving-service';

/** @type {Promise<boolean> | null} */
let running = null;

function routes() {
  // @ts-expect-error variable global del tema
  return typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
}

function cartUrl() {
  return `${routes().cart_url ?? '/cart'}.js`;
}

function addUrl() {
  const url = routes().cart_add_url ?? '/cart/add.js';
  return url.endsWith('.js') ? url : `${url}.js`;
}

function updateUrl() {
  const url = routes().cart_update_url ?? '/cart/update';
  return url.endsWith('.js') ? url : `${url}.js`;
}

async function getCart() {
  return fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((res) =>
    res.json()
  );
}

/** Secciones del carrito que hay en la página, para repintarlas en el mismo POST. */
function sectionIds() {
  const ids = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
  }
  return ids;
}

/**
 * Qué falta: servicios sin añadir y servicios con otra cantidad.
 *
 * @param {any} cart
 */
function pending(cart) {
  /** @type {Map<string, any[]>} */
  const children = new Map();
  for (const item of cart.items ?? []) {
    const parentKey = item.parent_relationship?.parent_key;
    if (!parentKey) continue;
    if (!children.has(parentKey)) children.set(parentKey, []);
    children.get(parentKey)?.push(item);
  }

  const adds = [];
  /** @type {Record<string, number>} */
  const updates = {};

  for (const item of cart.items ?? []) {
    const serviceId = item.properties?.[PROPERTY];
    if (!serviceId) continue;

    const service = (children.get(item.key) ?? []).find((child) => String(child.variant_id) === String(serviceId));
    if (!service) {
      adds.push({ id: Number(serviceId), quantity: item.quantity, parent_line_key: item.key });
    } else if (service.quantity !== item.quantity) {
      updates[service.key] = item.quantity;
    }
  }

  return { adds, updates };
}

/** @param {string} url @param {object} body */
async function post(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  }).then((res) => res.json());

  if (response?.status && response.status >= 400) {
    throw new Error(response.description || response.message || 'Cart request failed');
  }
  return response;
}

/**
 * Añade los servicios que falten y ajusta cantidades. Devuelve true si tocó
 * el carrito.
 */
export function repair() {
  if (running) return running;

  running = (async () => {
    const cart = await getCart();
    const { adds, updates } = pending(cart);
    const hasUpdates = Object.keys(updates).length > 0;
    if (!adds.length && !hasUpdates) return false;

    const sections = sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: adds.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      let response = null;
      if (hasUpdates) response = await post(updateUrl(), { updates, sections: sections.join(',') });
      if (adds.length) response = await post(addUrl(), { items: adds, sections: sections.join(',') });

      const fresh = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
        detail: {
          items: fresh.items,
          source: SOURCE,
          itemCount: 0,
          sections: response?.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      console.warn('[mr-engraving-service]', error);
    }

    return true;
  })().finally(() => {
    running = null;
  });

  return running;
}

/** Tras cada cambio del carrito que no venga de aquí mismo. */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // @ts-expect-error evento estándar con la promesa del carrito
  const promise = event.promise;
  if (!promise) return;

  promise
    .then((/** @type {any} */ result) => {
      if (result?.detail?.source === SOURCE) return;
      repair();
    })
    .catch(() => {
      /* el tema ya informa del error */
    });
});

/**
 * Antes de pagar: si falta algún servicio se añade primero y luego se sigue.
 * En window y en captura, para adelantarse al tema.
 */
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('[name="checkout"], #checkout') : null;
    if (!(button instanceof HTMLButtonElement) || button.dataset.mrServiceChecked === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    repair()
      .catch(() => false)
      .finally(() => {
        button.dataset.mrServiceChecked = 'true';
        button.click();
        setTimeout(() => {
          delete button.dataset.mrServiceChecked;
        }, 1000);
      });
  },
  { capture: true }
);

/* -------------------------------------------------------------------------- */
/* Ficha de producto                                                          */
/* -------------------------------------------------------------------------- */

/**
 * En la ficha, el bloque de grabado en modo servicio (mr-engraving con
 * data-mr-service) no cambia la variante del producto. Aquí se traduce lo que
 * eligió el cliente —tarjeta de zona y, si la hay, la casilla del otro
 * costado— a la variante del servicio: se guarda en el campo oculto
 * properties[_mr_servicio] y en data-mr-service-price, que suma el total del
 * botón de compra (assets/mr-buy-total.js).
 *
 * Corre después de los manejadores del propio bloque (escucha en document, en
 * burbuja), así lee el estado ya actualizado.
 *
 * @param {Element | null} element
 */
function syncEngraving(element) {
  if (!(element instanceof HTMLElement) || element.dataset.mrService !== 'true') return;

  const open = element.dataset.open === 'true';
  const card = element.querySelector('[data-mr-cards="engraving"] .mr-card__input:checked');
  const toggle = element.querySelector('[data-mr-second-toggle]');
  const wrap = element.querySelector('[data-mr-second-wrap]');
  const second =
    toggle instanceof HTMLInputElement && toggle.checked && wrap instanceof HTMLElement && !wrap.hidden;

  let id = '';
  let price = 0;

  if (open && card instanceof HTMLInputElement) {
    if (second && card.dataset.mrSecondServiceId) {
      id = card.dataset.mrSecondServiceId;
      price = Number(card.dataset.mrSecondServicePrice) || 0;
    } else {
      id = card.dataset.mrServiceId ?? '';
      price = Number(card.dataset.mrServicePrice) || 0;
    }
  }

  element.dataset.mrServicePrice = String(id ? price : 0);
  element.dataset.mrServiceCard = id && card instanceof HTMLInputElement ? card.value : '';
  element.dataset.mrServiceSecond = String(Boolean(id && second));

  const input = element.querySelector('[data-mr-service-input]');
  if (input instanceof HTMLInputElement) {
    input.value = id;
    input.disabled = !id;
  }

  element.closest('.shopify-section')?.dispatchEvent(new CustomEvent('mr:extras-changed'));
}

/** @param {Event} event */
function handleEngravingEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  syncEngraving(target.closest('mr-engraving[data-mr-service="true"]'));
}

document.addEventListener('change', handleEngravingEvent);
document.addEventListener('click', handleEngravingEvent);

/**
 * Al cambiar de color el tema repinta las tarjetas sin ninguna marcada (en
 * modo servicio la marca no sale de la variante). Se vuelve a marcar la que
 * había, y la casilla del otro costado, como si el cliente las tocara.
 */
document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;

    for (const element of document.querySelectorAll('mr-engraving[data-mr-service="true"]')) {
      if (!(element instanceof HTMLElement)) continue;
      const value = element.dataset.mrServiceCard ?? '';
      const second = element.dataset.mrServiceSecond === 'true';
      if (!value) continue;

      // @ts-expect-error evento estándar con la promesa de la variante
      event.promise
        ?.then(() => {
          requestAnimationFrame(() => {
            const card = [...element.querySelectorAll('[data-mr-cards="engraving"] .mr-card__input')].find(
              (input) => input instanceof HTMLInputElement && input.value === value
            );
            if (!(card instanceof HTMLInputElement) || card.disabled) {
              syncEngraving(element);
              return;
            }
            card.checked = true;
            card.dispatchEvent(new Event('change', { bubbles: true }));

            const toggle = element.querySelector('[data-mr-second-toggle]');
            const wrap = element.querySelector('[data-mr-second-wrap]');
            if (second && toggle instanceof HTMLInputElement && wrap instanceof HTMLElement && !wrap.hidden) {
              toggle.checked = true;
              toggle.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        })
        .catch(() => {
          /* si falla, se queda como estaba */
        });
    }
  },
  { capture: true }
);

repair();
